/**
 * Auth API Service
 * Centralized auth-related API calls
 */

import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { ApiResponse } from '../api-types';
import type {
  User,
  LoginRequest,
  LoginResult,
  RegisterRequest,
  ChangePasswordRequest,
  PasswordResetRequest as ForgotPasswordRequest,
  PasswordResetConfirmRequest as ResetPasswordRequest,
  AuthToken,
  SignupOtpSentResponse,
  RequestTwoFactorOtpResponse,
  CompleteTwoFactorLoginRequest,
  ActiveSession,
  AccountEmails,
  AccountMergeStarted,
} from '@welynk/types';

export interface TwoFactorStatusResponse {
  enabled: boolean;
  setupInProgress: boolean;
  backupCodesRemaining: number;
  setupAt?: string;
}

export interface TwoFactorSetupInitiateResponse {
  secret: string;
  qrCode: string;
  manualEntry: string;
  backupCodes: string[];
  message: string;
}

export interface TwoFactorVerifySetupResponse {
  success: boolean;
  backupCodes: string[];
  message: string;
}

export class AuthApiService {
  /**
   * Login user.
   * Returns AuthToken on success, or TwoFactorChallengeResponse when the server
   * needs the TOTP code before issuing tokens (discriminate on `requiresTwoFactor`).
   */
  static async login(credentials: LoginRequest): Promise<LoginResult> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<LoginResult>>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    );
    return response.data.data;
  }

  /**
   * Logout — revokes the refresh token server-side before clearing local state.
   * The token normally travels via the httpOnly cookie; pass it explicitly only
   * for non-cookie clients.
   */
  static async logout(refreshToken?: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.AUTH.LOGOUT, refreshToken ? { refreshToken } : {});
  }

  /**
   * Register user. Response includes the signup OTP reference (otpId) the
   * caller uses to verify/resend the emailed 6-digit code.
   */
  static async register(
    data: RegisterRequest
  ): Promise<User & Partial<SignupOtpSentResponse> & { alreadyRegistered?: boolean }> {
    const client = getApiClient();
    const response = await client.post<
      ApiResponse<User & Partial<SignupOtpSentResponse> & { alreadyRegistered?: boolean }>
    >(API_ENDPOINTS.USERS.CREATE, data);
    return response.data.data;
  }

  /**
   * Refresh access token. The refresh token normally travels via the httpOnly
   * cookie; pass it explicitly only for non-cookie clients.
   */
  static async refreshToken(refreshToken?: string): Promise<AuthToken> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<AuthToken>>(
      API_ENDPOINTS.AUTH.REFRESH,
      refreshToken ? { refreshToken } : {}
    );
    return response.data.data;
  }

  /**
   * Change password
   */
  static async changePassword(data: ChangePasswordRequest): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, data);
  }

  /**
   * Request password reset (send email with token)
   */
  static async requestPasswordReset(data: ForgotPasswordRequest): Promise<{ message: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      data
    );
    return response.data.data;
  }

  /**
   * Reset password using token
   */
  static async resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      data
    );
    return response.data.data;
  }

  /**
   * Verify the 6-digit signup OTP and activate the account
   */
  static async verifySignupOtp(otpId: string, code: string): Promise<{ message: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.AUTH.VERIFY_SIGNUP_OTP,
      { otpId, code }
    );
    return response.data.data;
  }

  /**
   * Resend the signup OTP (rate-limited server-side). Pass otpId when the
   * caller already has one (e.g. just registered); pass email when it
   * doesn't (e.g. login redirected here for an unverified account).
   */
  static async resendSignupOtp(
    params: { otpId: string } | { email: string }
  ): Promise<Partial<SignupOtpSentResponse> & { message: string }> {
    const client = getApiClient();
    const response = await client.post<
      ApiResponse<Partial<SignupOtpSentResponse> & { message: string }>
    >(API_ENDPOINTS.AUTH.RESEND_SIGNUP_OTP, params);
    return response.data.data;
  }

  /**
   * Request an emailed OTP for a 2FA-pending login
   */
  static async requestTwoFactorOtp(
    tempToken: string
  ): Promise<RequestTwoFactorOtpResponse & { message: string }> {
    const client = getApiClient();
    const response = await client.post<
      ApiResponse<RequestTwoFactorOtpResponse & { message: string }>
    >(API_ENDPOINTS.AUTH.TWO_FACTOR_REQUEST_OTP, { tempToken });
    return response.data.data;
  }

  /**
   * Complete a 2FA-pending login with a TOTP code, an email OTP, or a backup code
   */
  static async completeTwoFactorLogin(data: CompleteTwoFactorLoginRequest): Promise<AuthToken> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<AuthToken>>(
      API_ENDPOINTS.AUTH.TWO_FACTOR_LOGIN,
      data
    );
    return response.data.data;
  }

  /**
   * Get current user profile
   */
  static async getCurrentUser(): Promise<User> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<User>>(API_ENDPOINTS.AUTH.ME);
    return response.data.data;
  }

  /**
   * Get 2FA status for current user
   */
  static async getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<TwoFactorStatusResponse>>(
      API_ENDPOINTS.AUTH.TWO_FACTOR_STATUS
    );
    return response.data.data;
  }

  /**
   * Start 2FA setup and receive QR code / secret
   */
  static async initiateTwoFactorSetup(): Promise<TwoFactorSetupInitiateResponse> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<TwoFactorSetupInitiateResponse>>(
      API_ENDPOINTS.AUTH.TWO_FACTOR_SETUP_INITIATE,
      {}
    );
    return response.data.data;
  }

  /**
   * Verify 2FA setup with 6-digit code
   */
  static async verifyTwoFactorSetup(code: string): Promise<TwoFactorVerifySetupResponse> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<TwoFactorVerifySetupResponse>>(
      API_ENDPOINTS.AUTH.TWO_FACTOR_SETUP_VERIFY,
      { code }
    );
    return response.data.data;
  }

  /**
   * Disable 2FA for current user
   */
  static async disableTwoFactor(password: string): Promise<{ success: boolean; message: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ success: boolean; message: string }>>(
      API_ENDPOINTS.AUTH.TWO_FACTOR_DISABLE,
      { password }
    );
    return response.data.data;
  }

  /** The sign-in email and the college/work emails linked to the account. */
  static async getMyEmails(): Promise<AccountEmails> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<AccountEmails>>(API_ENDPOINTS.AUTH.MY_EMAILS);
    return response.data.data;
  }

  /** Merge step 1: the other account's email and password; a code goes to that email. */
  static async startMerge(email: string, password: string): Promise<AccountMergeStarted> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<AccountMergeStarted>>(
      API_ENDPOINTS.AUTH.MERGE_START,
      { email, password }
    );
    return response.data.data;
  }

  /** Merge step 2: the code; the other account's data moves here and it's closed. */
  static async completeMerge(otpId: string, code: string): Promise<{ mergedEmail: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ mergedEmail: string }>>(
      API_ENDPOINTS.AUTH.MERGE_COMPLETE,
      { otpId, code }
    );
    return response.data.data;
  }

  /** Starts a sign-in email change; a link goes to the new address to confirm it. */
  static async changeEmail(newEmail: string, password: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.AUTH.CHANGE_EMAIL, { newEmail, password });
  }

  /**
   * Confirm email change using token from the email link
   */
  static async confirmEmailChange(token: string): Promise<{ message: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.AUTH.CONFIRM_EMAIL_CHANGE,
      { token }
    );
    return response.data.data;
  }

  /**
   * List the caller's currently-active sessions/devices
   */
  static async getSessions(): Promise<ActiveSession[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<ActiveSession[]>>(API_ENDPOINTS.AUTH.SESSIONS);
    return response.data.data;
  }

  /**
   * Revoke a single session by id
   */
  static async revokeSession(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.AUTH.SESSION_BY_ID(id));
  }

  /**
   * Log out of every session except the one currently in use
   */
  static async revokeOtherSessions(): Promise<{ count: number }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ message: string; count: number }>>(
      API_ENDPOINTS.AUTH.SESSIONS_REVOKE_OTHERS,
      {}
    );
    return { count: response.data.data.count };
  }
}
